/**
 * O2 藝人管理列表：actors 與 artistAdminList 合併、篩選與排序 helpers. Pure — no React / Firebase — so node:test can
 * import them.
 */
import type { ArtistAdminOwner, ArtistAdminRow, ArtistProfileStatus } from "./artistAdmin";

export type ArtistListFilter = "all" | "published" | "verified" | "unclaimed" | "plain";

export const ARTIST_LIST_FILTERS: { value: ArtistListFilter; label: string; help: string }[] = [
  { value: "all", label: "全部", help: "所有藝人與演員" },
  { value: "published", label: "有公開專頁", help: "專頁狀態為已公開" },
  { value: "verified", label: "已認證", help: "App 目前顯示認證徽章" },
  { value: "unclaimed", label: "未開通", help: "已建立專頁（公開或下架），但還沒有藝人擁有者" },
  { value: "plain", label: "純演員", help: "尚未建立藝人專頁" },
];

/** An actors row with its artistAdminList row attached (undefined when the backend has no row or is unavailable). */
export type WithAdmin<T extends { id: string }> = T & { admin?: ArtistAdminRow };

/** Joins admin rows onto actors by id (actors doc id == artistProfiles doc id). */
export function mergeAdminRows<T extends { id: string }>(actors: T[], rows: ArtistAdminRow[] | undefined): WithAdmin<T>[] {
  if (!rows || rows.length === 0) return actors.map((a) => ({ ...a }));
  const byId = new Map(rows.map((r) => [r.artistId, r]));
  return actors.map((a) => {
    const admin = byId.get(a.id);
    return admin ? { ...a, admin } : { ...a };
  });
}

export const profileStatusOf = (admin: ArtistAdminRow | undefined): ArtistProfileStatus => admin?.profileStatus ?? "none";

export function matchesArtistFilter(admin: ArtistAdminRow | undefined, filter: ArtistListFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "published":
      return admin?.profileStatus === "published";
    case "verified":
      return admin?.verified === true;
    case "unclaimed":
      return !!admin && admin.profileStatus !== "none" && admin.owners.length === 0;
    case "plain":
      return profileStatusOf(admin) === "none";
    default:
      return true;
  }
}

export const filterArtists = <T extends { id: string }>(rows: WithAdmin<T>[], filter: ArtistListFilter): WithAdmin<T>[] =>
  filter === "all" ? rows : rows.filter((r) => matchesArtistFilter(r.admin, filter));

export const countByFilter = <T extends { id: string }>(rows: WithAdmin<T>[]): Record<ArtistListFilter, number> => {
  const out = { all: 0, published: 0, verified: 0, unclaimed: 0, plain: 0 } as Record<ArtistListFilter, number>;
  for (const r of rows) for (const f of ARTIST_LIST_FILTERS) if (matchesArtistFilter(r.admin, f.value)) out[f.value] += 1;
  return out;
};

/** "王小明" / "a@b.com" / uid for the first owner; null when there is none. */
export const ownerLabel = (o: ArtistAdminOwner | undefined): string | null =>
  o ? o.displayName?.trim() || o.email?.trim() || o.uid : null;

/** Extra searchable text from the admin row (專頁名稱、擁有者名稱與 Email). */
export const adminSearchText = (admin: ArtistAdminRow | undefined): string =>
  admin ? [admin.displayName ?? "", ...admin.owners.flatMap((o) => [o.displayName ?? "", o.email ?? ""])].join(" ").toLowerCase() : "";

const STATUS_RANK: Record<ArtistProfileStatus, number> = { published: 0, unpublished: 1, none: 2 };

/** Comparators for AdminDataTable sortFn (ascending; the table applies direction). Missing admin rows sort as 0 / none. */
export const artistSorters = {
  profile: (a: { admin?: ArtistAdminRow }, b: { admin?: ArtistAdminRow }) =>
    STATUS_RANK[profileStatusOf(a.admin)] - STATUS_RANK[profileStatusOf(b.admin)],
  verified: (a: { admin?: ArtistAdminRow }, b: { admin?: ArtistAdminRow }) =>
    Number(b.admin?.verified ?? false) - Number(a.admin?.verified ?? false),
  owners: (a: { admin?: ArtistAdminRow }, b: { admin?: ArtistAdminRow }) =>
    (a.admin?.owners.length ?? 0) - (b.admin?.owners.length ?? 0),
  followers: (a: { admin?: ArtistAdminRow }, b: { admin?: ArtistAdminRow }) =>
    (a.admin?.followerCount ?? 0) - (b.admin?.followerCount ?? 0),
  works: (a: { admin?: ArtistAdminRow }, b: { admin?: ArtistAdminRow }) =>
    (a.admin?.worksCount ?? 0) - (b.admin?.worksCount ?? 0) ||
    (a.admin?.unreleasedWorksCount ?? 0) - (b.admin?.unreleasedWorksCount ?? 0),
};
